import { useState } from 'react';

export type AssetPhotoProps = {
  /** Public URL of the photo. `null` or empty falls back to the IMG
   *  placeholder. */
  src: string | null | undefined;
  /** Descriptive alt text — typically the asset name. */
  alt: string;
  /** Visual tier. `card` = 48px (Fleet card), `header` = 64px (S5 hero). */
  size?: 'card' | 'header';
};

const SIZE_CLASS: Record<NonNullable<AssetPhotoProps['size']>, string> = {
  card: 'h-12 w-12',
  header: 'h-16 w-16',
};

/**
 * Asset photo thumbnail used on Fleet cards + S5 header. Gracefully
 * degrades to an IMG placeholder when the source URL is missing or
 * the image fails to load. The outer wrapper is keyed on `src` so a
 * stale `errored` state can never bleed across asset changes — the
 * inner component is fully remounted, not just re-rendered.
 *
 * This replaces two duplicate inline helpers (the old `AssetPhoto`
 * inside S5SignalAnalysis.tsx and `AssetCardPhoto` inside
 * AssetCard.tsx). Same fallback behaviour; same accessibility
 * surface; consolidated so future tweaks land in one place.
 */
export function AssetPhoto(props: AssetPhotoProps) {
  // Key on src so the inner component fully remounts when the asset
  // changes — no stale loaded/errored state can leak across.
  return <AssetPhotoImpl key={props.src ?? '_none'} {...props} />;
}

function AssetPhotoImpl({ src, alt, size = 'card' }: AssetPhotoProps) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  if (!src || errored) {
    return (
      <div
        aria-hidden
        className={
          'flex shrink-0 items-center justify-center rounded-r-default border border-gray-300 bg-gray-100 font-mono text-fs-meta text-gray-700 ' +
          SIZE_CLASS[size]
        }
      >
        IMG
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={
        'shrink-0 rounded-r-default border border-gray-300 object-cover transition-opacity ' +
        SIZE_CLASS[size] +
        (loaded ? ' opacity-100' : ' opacity-0')
      }
      onLoad={() => setLoaded(true)}
      onError={() => setErrored(true)}
    />
  );
}
